import { useGraphqlState } from "./useGraphqlState.js";
import { hash } from "ohash";
import { getEndpoint } from "#nuxt-graphql-middleware/helpers";
import { useNuxtApp, useAppConfig } from "#imports";
import { operationHashes } from "#nuxt-graphql-middleware/operation-hashes";
import {
  clientCacheEnabledAtBuild,
  importMetaClient
} from "#nuxt-graphql-middleware/config";
import { encodeVariables, sortQueryParams } from "../helpers/queryEncoding.js";
import { encodeContext, getOrCreateClientCache } from "../helpers/composables.js";
import { OPERATION_HASH_PREFIX } from "../settings/index.js";
export function performRequest(operation, operationName, variablesOrBody, overrideFetchOptions, globalClientContext, overrideClientContext, cacheOptions, asyncDataKey) {
  const state = useGraphqlState();
  const app = useNuxtApp();
  const config = useAppConfig();
  const method = operation === "query" ? "get" : "post";
  const isQuery = operation === "query";
  if (!state) {
    console.error(
      `A GraphQL composable for ${operation} "${operationName}" was called before the "nuxt-graphql-middleware-provide-state" plugin could provide the state, which might lead to unexpected behaviour. Make sure that custom plugins that perform GraphQL requests are executed after "nuxt-graphql-middleware-provide-state" by setting it as a dependency via "dependsOn".`
    );
  }
  const clientContext = Object.assign(
    {},
    globalClientContext,
    overrideClientContext
  );
  const fetchOptions = Object.assign(
    {},
    state?.fetchOptions,
    overrideFetchOptions
  );
  const paramsRaw = Object.assign(
    importMetaClient ? {
      // The unique operation hash that changes whenever any operation source or
      // fragment changes.
      [OPERATION_HASH_PREFIX]: operationHashes[operationName]
    } : {},
    encodeContext(clientContext),
    fetchOptions.params,
    fetchOptions.query,
    isQuery ? encodeVariables(variablesOrBody) : null
  );
  const params = importMetaClient && isQuery ? sortQueryParams(paramsRaw) : paramsRaw;
  const cacheKey = importMetaClient && clientCacheEnabledAtBuild && isQuery && cacheOptions?.client && config.graphqlMiddleware.clientCacheEnabled ? `${operation}:${operationName}:${hash(params)}` : void 0;
  if (importMetaClient && cacheKey) {
    const cache = getOrCreateClientCache(app, config);
    if (cache) {
      const cached = cache.get(cacheKey);
      if (cached) {
        if (import.meta.dev) {
          cached.then((response) => {
            if (response.errors.length) {
              app.callHook("nuxt-graphql-middleware:errors", {
                operation,
                operationName,
                errors: response.errors,
                stack: Error().stack
              });
            }
          });
        }
        return cached;
      }
    }
  }
  const promise = $fetch(
    getEndpoint(operation, operationName),
    Object.assign(
      {},
      // Use the merged fetch options.
      fetchOptions,
      // Remove params and query from the fetch options.
      {
        params: void 0,
        query: void 0
      },
      // Set the previously merged params. That way we only ever pass "params"
      // as the query params.
      {
        params,
        method,
        body: operation === "mutation" ? variablesOrBody : void 0
      }
    )
  ).then((v) => {
    if (import.meta.dev && v?.errors?.length) {
      app.callHook("nuxt-graphql-middleware:errors", {
        operation,
        operationName,
        errors: v.errors,
        stack: Error().stack
      });
    }
    return Object.assign({}, v, {
      data: v?.data,
      errors: v?.errors || []
    });
  });
  if (importMetaClient && cacheKey && app.$graphqlCache && clientCacheEnabledAtBuild) {
    app.$graphqlCache.set(cacheKey, promise, asyncDataKey);
  }
  return promise;
}
